import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Edit2, Save, X, StickyNote } from 'lucide-react';
import { formatDateTimeAU } from '@/lib/utils';

export default function NotesBlock({ 
  notes = '', 
  updatedAt, 
  updatedBy, 
  onSave, 
  title = 'Notes',
  placeholder = 'Add notes here...',
  className = ''
}) {
  const [editing, setEditing] = useState(false);
  const [localNotes, setLocalNotes] = useState(notes || '');
  const [saving, setSaving] = useState(false);

  // When entering edit mode, sync with current notes prop
  const startEditing = () => {
    setLocalNotes(notes || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localNotes);
      setEditing(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setLocalNotes(notes || '');
    setEditing(false);
  };

  return (
    <div className={`caffeine-card p-6 ${className}`} data-testid="notes-block">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-[#4A3B32]" />
          <h3 className="font-heading text-lg text-[#2C2420]">{title}</h3>
        </div>
        {!editing ? (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={startEditing}
            className="caffeine-btn-ghost"
            data-testid="edit-notes-btn"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleCancel}
              className="caffeine-btn-ghost text-[#8C8078]"
              data-testid="cancel-notes-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="caffeine-btn-ghost text-[#6B8E6B]"
              data-testid="save-notes-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <Textarea
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          placeholder={placeholder}
          className="caffeine-input min-h-[120px]"
          autoFocus
          data-testid="notes-textarea"
        />
      ) : (
        <div className="min-h-[60px]">
          {notes ? (
            <p className="text-[#2C2420] whitespace-pre-wrap">{notes}</p>
          ) : (
            <p className="text-[#8C8078] italic">{placeholder}</p>
          )}
        </div>
      )}

      {updatedAt && (
        <p className="text-xs text-[#8C8078] mt-3">
          Last updated: {formatDateTimeAU(updatedAt)}
          {updatedBy && ` by ${updatedBy}`}
        </p>
      )}
    </div>
  );
}
